import { supabase } from '../supabase';
import type {
  BulkAddUserGroupMembershipsResult,
  GroupMemberRow,
  GroupMembershipBadgeRow,
  UserGroupMembershipRow,
} from '../types/database';
import type {
  BulkAddUserGroupsInput,
  SetUserGroupsInput,
} from '../types/memberships.types';

function getRpcErrorMessage(errorMessage: string): string {
  const cleanMessage = errorMessage.replace(/^ERROR:\s*/i, '').trim();
  return cleanMessage || 'Membership request failed.';
}

export async function listUserGroupMemberships(
  userId: string
): Promise<UserGroupMembershipRow[]> {
  const { data, error } = await supabase.rpc('list_user_group_memberships', {
    p_user_id: userId,
  });

  if (error) {
    throw new Error(
      `Failed to load user groups: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as UserGroupMembershipRow[];
}

export async function setUserGroupMemberships(
  input: SetUserGroupsInput
): Promise<UserGroupMembershipRow[]> {
  const { data, error } = await supabase.rpc('set_user_group_memberships', {
    p_user_id: input.userId,
    p_group_ids: input.groupIds,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  return (data ?? []) as UserGroupMembershipRow[];
}

export async function bulkAddUserGroupMemberships(
  input: BulkAddUserGroupsInput
): Promise<BulkAddUserGroupMembershipsResult> {
  const { data, error } = await supabase.rpc(
    'bulk_add_user_group_memberships',
    {
      p_user_ids: input.userIds,
      p_group_ids: input.groupIds,
    }
  );

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  const result = (data as BulkAddUserGroupMembershipsResult[] | null)?.[0];

  if (!result) {
    throw new Error('Bulk group assignment did not return a result.');
  }

  return result;
}

export async function listGroupMembershipsForUsers(
  userIds: string[]
): Promise<GroupMembershipBadgeRow[]> {
  if (userIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase.rpc(
    'list_group_memberships_for_users',
    {
      p_user_ids: userIds,
    }
  );

  if (error) {
    throw new Error(
      `Failed to load group badges: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as GroupMembershipBadgeRow[];
}

export async function listGroupMembers(
  groupId: string
): Promise<GroupMemberRow[]> {
  const { data, error } = await supabase.rpc('list_group_members', {
    p_group_id: groupId,
  });

  if (error) {
    throw new Error(
      `Failed to load group members: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as GroupMemberRow[];
}
